/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Passwords } from "@/sergecych.uni3/Passwords";
import { MyoRegistration, MyoService } from "@/lib/MyoService";
import { encode64url, timeout, utf8ToBytes } from "@/sergecych.uni3/tools";
import { bossDump } from "@/sergecych.uni3/SimpleBoss";
import { UniversalSymmetricKey } from "@/sergecych.uni3/UnversalKeys";
import { encode64 } from "universa-minicrypto";
import { Coffer } from "@/sergecych.uni3/Coffer";
import { TimeService } from "@/sergecych.uni3/TimeService";

class Deferred<T> {
  private rejectHandler?: (arg0?: any) => void;
  private resolveHandler?: (value?: T | PromiseLike<T> | undefined) => void;

  readonly promise: Promise<T>;

  constructor() {
    this.promise = new Promise<T>((resolve, reject) => {
      this.resolveHandler = resolve;
      this.rejectHandler = reject;
    });
  }

  resolve(reslut: T) {
    this.resolveHandler!(reslut);
  }

  reject(error: Error | string) {
    this.rejectHandler!(error);
  }
}

export class QRPass {
  private static _instance?: QRPass;

  login?: string;
  password?: string;
  private registration?: MyoRegistration;
  private deferredService = new Deferred<MyoService>();

  static initialize() {
    let password = localStorage.getItem("password") ?? undefined;
    let login = localStorage.getItem("login") ?? undefined;
    if (!password || !login) {
      console.log("No saved login, will create new myo account");
      // in the future we might ask user to restore account
      login = password = undefined;
    }
    QRPass._instance = new QRPass(login, password);
  }

  static get instance(): QRPass {
    if (!this._instance) this.initialize();
    return this._instance!;
  }

  static getService(): Promise<MyoService> {
    return this.instance.getService();
  }

  /**
   * Constructor either set this.registraton or this.serviceLogin fieds and starts
   * background key generation or login.
   * @param login
   * @param password
   */
  constructor(login?: string, password?: string) {
    if (!login || !password) {
      this.createNewRegistration();
    } else {
      this.login = login;
      this.password = password;
    }
  }

  private async createNewRegistration() {
    console.debug("preparing anonymous login to myoservice");
    this.login = Passwords.randomId(17);
    this.password = Passwords.randomId(17);
    this.registration = MyoService.createRegistration(4096);
  }

  private _sericeConnectStarted = false;

  async createDocument(data: any): Promise<string> {
    const ms = await this.getService();
    const root = window.location.origin;
    // the black magic starts here ;)
    const randomKey = UniversalSymmetricKey.createRandom();
    // this coffer is only accessible to the account owner
    const coffer = ms.createCoffer();
    // now it is also readable by new key
    coffer.addKeys(randomKey);
    // so we pack document in it:
    coffer.setPayload(data);
    // and store it in our private cloud:
    let result = await ms.call("setByUniqueTag", {
      uniqueTag: "QRPass",
      head: coffer.pack()
    });
    console.log(result);
    // and we create a handle that will keep tag information for a key:
    result = await ms.call("createHandle", {
      elementId: result.element.id,
      keyInfo: encode64(randomKey.tag.id)
    });
    console.log(result);
    // and finally we combine key and handle id to make a read link:
    return `${root}/h/${result.handle.id}#${encode64url(randomKey.keyBytes)}`;
  }

  getService(): Promise<MyoService> {
    // we want it to run asynchrously
    timeout(100).then(async () => {
      if (!this._sericeConnectStarted) {
        console.log("connecting to myonly.cloud service...");
        this._sericeConnectStarted = true;
        if (!this.registration) {
          console.log("we have credentials lets try them");
          try {
            const s = await MyoService.signIn(this.login!, this.password!);
            console.log("login successful");
            this.deferredService.resolve(s);
          } catch (e) {
            // todo: check e and retry or re-register
            console.warn("failed to sign in:", e);
            this._sericeConnectStarted;
            return this.getService();
          }
        } else {
          // complete registration:
          try {
            // automatic password: need not many rounds. but with used password we should use really big one ;)
            this.registration.setPassword(this.password!, 100);
            const s = await this.registration.performWithLogin(this.login!);
            console.log("registration successful, saving credentials");
            localStorage.setItem("login", this.login!);
            localStorage.setItem("password", this.password!);
            console.log("registration done");
            this.deferredService.resolve(s);
            console.log("after registration", this.deferredService.promise);
          } catch (e) {
            console.warn("registration failed", e);
            this._sericeConnectStarted = false;
            if (e instanceof TypeError) {
              console.log("most likely network failure");
              if (e.message.match(/failed to fetch/i))
                this.deferredService.reject(
                  "Connection failed, please retry later."
                );
              else
                this.deferredService.reject(
                  new Error(`Connection problem (${e})`)
                );
              return this.deferredService.promise;
            } else {
              console.log(
                "Something is bad with the registration, we weill create new one"
              );
              this.createNewRegistration();
            }
            return this.getService();
          }
        }
      }
    });
    return this.deferredService.promise;
  }
}
